import { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../../services/api';
import { toast } from '../../hooks/useToast';
import { TasksSkeleton } from '../Skeleton';
import {
  Plus, Trash2, Calendar, ChevronRight, ChevronLeft,
  Lightbulb, ClipboardList, Loader2, CheckCircle2,
  Search, X, Filter, AlertTriangle, Archive,
  ArrowUp, ArrowRight as ArrowMid, ArrowDown,
  FileText, Clock, Flag, Download, Check,
} from 'lucide-react';

interface Task {
  id: number;
  title: string;
  description: string;
  status: string;
  priority: number;
  deadline: string | null;
  note_id: number | null;
  parent_id: number | null;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

interface NoteRef {
  id: number;
  title: string;
}

const STATUS_CONFIG = {
  idea:        { label: 'Идея',      color: 'text-purple-400', bg: 'bg-purple-500/10 border-purple-500/20', headerBg: 'bg-gradient-to-r from-purple-600/20 to-purple-600/5', icon: Lightbulb, accent: '#a855f7' },
  planned:     { label: 'В планах',  color: 'text-blue-400',   bg: 'bg-blue-500/10 border-blue-500/20',    headerBg: 'bg-gradient-to-r from-blue-600/20 to-blue-600/5',   icon: ClipboardList, accent: '#3b82f6' },
  in_progress: { label: 'В работе',  color: 'text-amber-400',  bg: 'bg-amber-500/10 border-amber-500/20',  headerBg: 'bg-gradient-to-r from-amber-600/20 to-amber-600/5', icon: Loader2, accent: '#f59e0b' },
  done:        { label: 'Сделано',   color: 'text-green-400',  bg: 'bg-green-500/10 border-green-500/20',   headerBg: 'bg-gradient-to-r from-green-600/20 to-green-600/5',  icon: CheckCircle2, accent: '#22c55e' },
} as const;

const KANBAN_STATUSES: (keyof typeof STATUS_CONFIG)[] = ['idea', 'planned', 'in_progress'];
const ALL_STATUSES = Object.keys(STATUS_CONFIG) as (keyof typeof STATUS_CONFIG)[];

const PRIORITY_CONFIG = [
  { value: 0, label: 'Без приоритета', icon: null, color: 'text-slate-500' },
  { value: 1, label: 'Низкий', icon: ArrowDown, color: 'text-green-400' },
  { value: 2, label: 'Средний', icon: ArrowMid, color: 'text-amber-400' },
  { value: 3, label: 'Высокий', icon: ArrowUp, color: 'text-red-400' },
];

type FilterMode = 'all' | 'overdue' | 'today' | 'week' | 'no-deadline';
type PriorityFilter = 'all' | '3' | '2' | '1';

const isOverdue = (deadline: string | null) => {
  if (!deadline) return false;
  return new Date(deadline) < new Date(new Date().toDateString());
};

const isToday = (deadline: string | null) => {
  if (!deadline) return false;
  return new Date(deadline).toDateString() === new Date().toDateString();
};

// ─── Task Modal ───────────────────────────────────────────
interface TaskModalProps {
  task: Task;
  notes: NoteRef[];
  allTasks: Task[];
  onClose: () => void;
  onSave: (id: number, data: Partial<Task>) => Promise<void>;
  onDelete: (id: number) => Promise<void>;
  onNavigateToNote?: (noteId: number) => void;
  onCreateSubtask: (title: string, parentId: number) => Promise<void>;
  onReload: () => Promise<void>;
}

function TaskModal({ task, notes, allTasks, onClose, onSave, onDelete, onNavigateToNote, onCreateSubtask, onReload }: TaskModalProps) {
  const [title, setTitle] = useState(task.title);
  const [description, setDescription] = useState(task.description);
  const [status, setStatus] = useState(task.status);
  const [priority, setPriority] = useState(task.priority);
  const [deadline, setDeadline] = useState(task.deadline || '');
  const [noteId, setNoteId] = useState<number | null>(task.note_id);
  const [saving, setSaving] = useState(false);
  const [subtaskTitle, setSubtaskTitle] = useState('');

  // Close on Esc
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const handleSave = async () => {
    setSaving(true);
    await onSave(task.id, {
      title, description, status,
      priority, deadline: deadline || null,
      note_id: noteId,
    });
    setSaving(false);
    onClose();
  };

  const handleDelete = async () => {
    if (!confirm('Удалить задачу?')) return;
    await onDelete(task.id);
    onClose();
  };

  const statusCfg = STATUS_CONFIG[status as keyof typeof STATUS_CONFIG];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      <div
        className="relative w-full max-w-lg bg-slate-900 border border-slate-700/80 rounded-2xl shadow-2xl animate-scale-in overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Status color header bar */}
        <div className="h-1" style={{ background: statusCfg?.accent || '#6366f1' }} />

        {/* Header */}
        <div className="px-5 pt-4 pb-3 border-b border-slate-800">
          <div className="flex items-start gap-3">
            <div className="flex-1">
              <input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="w-full text-[17px] font-semibold bg-transparent text-white focus:outline-none placeholder:text-slate-600"
                placeholder="Название задачи"
              />
              <div className="flex items-center gap-2 mt-1.5">
                <span className={`text-xs px-2 py-0.5 rounded-full border ${statusCfg?.bg}`} style={{ color: statusCfg?.accent }}>
                  {statusCfg?.label}
                </span>
                {task.deadline && (
                  <span className={`text-xs flex items-center gap-1 ${isOverdue(task.deadline) ? 'text-red-400' : 'text-slate-500'}`}>
                    <Calendar size={11} /> {new Date(task.deadline).toLocaleDateString('ru-RU')}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="p-5 space-y-4">
          {/* Description */}
          <div>
            <label className="text-xs text-slate-500 mb-1.5 block">Описание</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Добавьте описание задачи..."
              rows={4}
              className="w-full bg-slate-800/60 border border-slate-700 rounded-xl px-3 py-2.5 text-sm text-slate-300 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 resize-none leading-relaxed"
            />
          </div>

          {/* Status + Priority row */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-500 mb-1.5 block">Статус</label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="w-full bg-slate-800 border border-slate-700 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/50"
              >
                {ALL_STATUSES.map((s) => (
                  <option key={s} value={s}>{STATUS_CONFIG[s].label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs text-slate-500 mb-1.5 block">Приоритет</label>
              <div className="flex gap-1.5">
                {PRIORITY_CONFIG.map((p) => {
                  const Icon = p.icon;
                  return (
                    <button
                      key={p.value}
                      onClick={() => setPriority(p.value)}
                      className={`flex-1 flex items-center justify-center gap-1 py-2.5 rounded-xl border text-xs transition-all ${
                        priority === p.value
                          ? `${p.color} border-current bg-current/10`
                          : 'border-slate-700 text-slate-500 hover:border-slate-600'
                      }`}
                      title={p.label}
                    >
                      {Icon ? <Icon size={14} /> : <Flag size={12} />}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Deadline + Note link */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-500 mb-1.5 flex items-center gap-1 block">
                <Calendar size={11} /> Дедлайн
              </label>
              <input
                type="date"
                value={deadline}
                onChange={(e) => setDeadline(e.target.value)}
                className="w-full bg-slate-800 border border-slate-700 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/50"
              />
            </div>
            <div>
              <label className="text-xs text-slate-500 mb-1.5 flex items-center gap-1 block">
                <FileText size={11} /> Связанная заметка
              </label>
              <select
                value={noteId ?? ''}
                onChange={(e) => setNoteId(e.target.value ? Number(e.target.value) : null)}
                className="w-full bg-slate-800 border border-slate-700 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/50"
              >
                <option value="">— Нет —</option>
                {notes.map((n) => (
                  <option key={n.id} value={n.id}>{n.title || `Заметка #${n.id}`}</option>
                ))}
              </select>
              {noteId && onNavigateToNote && (
                <button
                  onClick={() => { onClose(); onNavigateToNote(noteId); }}
                  className="mt-1 text-xs text-indigo-400 hover:text-indigo-300 flex items-center gap-1 transition-colors"
                >
                  <FileText size={10} /> Открыть заметку →
                </button>
              )}
            </div>
          </div>

          {/* Subtasks */}
          {(() => {
            const subtasks = allTasks.filter(t => t.parent_id === task.id);
            const doneCount = subtasks.filter(s => s.status === 'done').length;
            const progress = subtasks.length > 0 ? (doneCount / subtasks.length) * 100 : 0;
            return (
              <div>
                <label className="text-xs text-slate-500 mb-1.5 flex items-center gap-1 block">
                  <CheckCircle2 size={11} /> Подзадачи {subtasks.length > 0 && <span className="text-slate-600">({doneCount}/{subtasks.length})</span>}
                </label>
                {subtasks.length > 0 && (
                  <div className="w-full h-1.5 bg-slate-800 rounded-full mb-2 overflow-hidden">
                    <div className="h-full bg-green-500 rounded-full transition-all" style={{ width: `${progress}%` }} />
                  </div>
                )}
                <div className="space-y-1 mb-2">
                  {subtasks.map(sub => (
                    <div key={sub.id} className="flex items-center gap-2 px-2 py-1 rounded-lg bg-slate-800/40 group">
                      <button
                        onClick={async () => {
                          const newStatus = sub.status === 'done' ? 'idea' : 'done';
                          await onSave(sub.id, { status: newStatus });
                          if (newStatus === 'done' && task.status !== 'done') {
                            const remaining = subtasks.filter(s => s.id !== sub.id && s.status !== 'done');
                            if (remaining.length === 0) {
                              await onSave(task.id, { status: 'done' });
                              toast('Все подзадачи выполнены — задача закрыта ✓', 'success');
                            }
                          }
                          await onReload();
                        }}
                        className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 transition-colors ${sub.status === 'done' ? 'bg-green-500/20 border-green-500 text-green-400' : 'border-slate-600 hover:border-slate-400'}`}
                      >
                        {sub.status === 'done' && <Check size={10} />}
                      </button>
                      <span className={`flex-1 text-xs ${sub.status === 'done' ? 'line-through text-slate-600' : 'text-slate-300'}`}>{sub.title}</span>
                      <button onClick={async () => { await onDelete(sub.id); await onReload(); }} className="text-slate-600 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-all">
                        <Trash2 size={11} />
                      </button>
                    </div>
                  ))}
                </div>
                <div className="flex gap-2">
                  <input
                    value={subtaskTitle}
                    onChange={(e) => setSubtaskTitle(e.target.value)}
                    onKeyDown={async (e) => {
                      if (e.key === 'Enter' && subtaskTitle.trim()) {
                        await onCreateSubtask(subtaskTitle.trim(), task.id);
                        setSubtaskTitle('');
                        await onReload();
                      }
                    }}
                    placeholder="+ Добавить подзадачу..."
                    className="flex-1 bg-slate-800/60 border border-slate-700 rounded-lg px-2.5 py-1.5 text-xs text-slate-300 focus:outline-none focus:ring-1 focus:ring-indigo-500/50 placeholder:text-slate-600"
                  />
                </div>
              </div>
            );
          })()}

          {/* Meta info */}
          <div className="flex items-center gap-4 text-[10px] text-slate-600 pt-0.5 border-t border-slate-800">
            <span className="flex items-center gap-1 pt-2"><Clock size={10} /> Создана: {new Date(task.created_at).toLocaleString('ru-RU')}</span>
            <span className="pt-2">Обновлена: {new Date(task.updated_at).toLocaleString('ru-RU')}</span>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-5 py-3 border-t border-slate-800 bg-slate-800/30">
          <button
            onClick={handleDelete}
            className="flex items-center gap-1.5 px-3 py-2 text-red-400 hover:bg-red-500/10 rounded-xl text-sm transition-colors"
          >
            <Trash2 size={14} /> Удалить
          </button>
          <div className="flex gap-2">
            <button onClick={onClose} className="px-4 py-2 text-sm text-slate-400 hover:text-white rounded-xl transition-colors">
              Отмена
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-5 py-2 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 text-white text-sm rounded-xl transition-colors font-medium"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : 'Сохранить'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Main component ─────────────────────────────────────────
interface TasksViewProps {
  onNavigateToNote?: (noteId: number) => void;
  pendingAction?: 'create' | null;
  onActionHandled?: () => void;
}

export function TasksView({ onNavigateToNote, pendingAction, onActionHandled }: TasksViewProps) {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [notes, setNotes] = useState<NoteRef[]>([]);
  const [showAdd, setShowAdd] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newStatus, setNewStatus] = useState<string>('idea');
  const [newDeadline, setNewDeadline] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [newPriority, setNewPriority] = useState(0);
  const [creating, setCreating] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [search, setSearch] = useState('');
  const [filterMode, setFilterMode] = useState<FilterMode>('all');
  const [priorityFilter, setPriorityFilter] = useState<PriorityFilter>('all');
  const [showFilters, setShowFilters] = useState(false);
  const [showArchive, setShowArchive] = useState(false);
  const [modalTask, setModalTask] = useState<Task | null>(null);
  const [dragTask, setDragTask] = useState<Task | null>(null);
  const [dragOver, setDragOver] = useState<string | null>(null);
  const [dragOverTaskId, setDragOverTaskId] = useState<number | null>(null);
  const [lastMovedId, setLastMovedId] = useState<number | null>(null);
  const [quickCreateStatus, setQuickCreateStatus] = useState<string | null>(null);
  const [quickCreateTitle, setQuickCreateTitle] = useState('');
  const [editingDeadlineId, setEditingDeadlineId] = useState<number | null>(null);
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(async () => {
    try {
      const [tRes, nRes] = await Promise.all([
        apiFetch('/api/tasks'),
        apiFetch('/api/notes'),
      ]);
      const [tData, nData] = await Promise.all([tRes.json(), nRes.json()]);
      if (tData.success) setTasks(tData.data);
      if (nData.success) setNotes(nData.data.map((n: { id: number; title: string }) => ({ id: n.id, title: n.title })));
    } catch {
      setLoadError('Не удалось загрузить задачи');
    }
    setLoaded(true);
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const handler = () => load();
    window.addEventListener('app:refresh', handler);
    return () => window.removeEventListener('app:refresh', handler);
  }, [load]);

  // Handle hotkey actions from App
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (pendingAction === 'create') { setShowAdd(true); onActionHandled?.(); }
  }, [pendingAction]);

  const createTask = async () => {
    if (!newTitle.trim()) return;
    setCreating(true);
    try {
      const res = await apiFetch('/api/tasks', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: newTitle.trim(),
          status: newStatus,
          deadline: newDeadline || undefined,
          description: newDescription,
          priority: newPriority,
        }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'Ошибка');
      toast('Задача создана');
    } catch (e: unknown) {
      toast(e instanceof Error ? e.message : 'Ошибка создания', 'error');
      setCreating(false);
      return;
    }
    setNewTitle('');
    setNewDeadline('');
    setNewDescription('');
    setNewPriority(0);
    setShowAdd(false);
    setCreating(false);
    await load();
  };

  const updateStatus = async (task: Task, status: string) => {
    await apiFetch(`/api/tasks/${task.id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status }),
    });
    setLastMovedId(task.id);
    await load();
    const cfg = STATUS_CONFIG[status as keyof typeof STATUS_CONFIG];
    toast(`→ ${cfg?.label || status}`);
  };

  const updateTask = async (id: number, data: Partial<Task>) => {
    await apiFetch(`/api/tasks/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    await load();
  };

  const deleteTask = async (id: number) => {
    await apiFetch(`/api/tasks/${id}`, { method: 'DELETE' });
    await load();
    toast('Задача удалена');
  };

  const isThisWeek = (deadline: string | null) => {
    if (!deadline) return false;
    const d = new Date(deadline);
    const now = new Date();
    const weekEnd = new Date(now);
    weekEnd.setDate(now.getDate() + 7);
    return d >= now && d <= weekEnd;
  };

  // Filter + search (exclude done from kanban)
  const filterTask = (t: Task) => {
    if (search) {
      const q = search.toLowerCase();
      if (!t.title.toLowerCase().includes(q) && !t.description.toLowerCase().includes(q)) return false;
    }
    if (priorityFilter !== 'all' && t.priority !== Number(priorityFilter)) return false;
    switch (filterMode) {
      case 'overdue': return isOverdue(t.deadline) && t.status !== 'done';
      case 'today': return isToday(t.deadline);
      case 'week': return isThisWeek(t.deadline);
      case 'no-deadline': return !t.deadline;
      default: return true;
    }
  };

  const rootTasks = tasks.filter(t => !t.parent_id);
  const kanbanTasks = rootTasks.filter(t => t.status !== 'done').filter(filterTask);
  const archiveTasks = rootTasks.filter(t => t.status === 'done').filter(filterTask);

  const tasksByStatus = KANBAN_STATUSES.map((s) => ({
    status: s,
    config: STATUS_CONFIG[s],
    items: kanbanTasks.filter((t) => t.status === s),
  }));

  const totalActive = tasks.filter(t => t.status !== 'done').length;
  const totalDone = tasks.filter(t => t.status === 'done').length;
  const totalOverdue = tasks.filter(t => isOverdue(t.deadline) && t.status !== 'done').length;

  const exportTasksCSV = () => {
    const rows = [
      ['ID', 'Название', 'Описание', 'Статус', 'Приоритет', 'Срок', 'Создано'],
      ...tasks.map(t => [
        String(t.id), t.title, t.description || '',
        t.status, String(t.priority), t.deadline || '', t.created_at,
      ]),
    ];
    const csv = rows.map(r => r.map(v => `"${v.replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `tasks_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  // Drag handlers
  const handleDragStart = (task: Task) => { setDragTask(task); };
  const handleDragOver = (e: React.DragEvent, status: string) => { e.preventDefault(); setDragOver(status); };
  const handleDragLeave = () => { setDragOver(null); };
  const handleDragOverTask = (e: React.DragEvent, taskId: number) => { e.preventDefault(); setDragOverTaskId(taskId); };
  const handleDrop = async (e: React.DragEvent, status: string) => {
    e.preventDefault();
    if (!dragTask) { setDragOver(null); setDragOverTaskId(null); setDragTask(null); return; }

    if (dragTask.status !== status) {
      // Move to different column
      await updateStatus(dragTask, status);
    } else if (dragOverTaskId && dragOverTaskId !== dragTask.id) {
      // Reorder within column
      const colItems = tasks.filter(t => t.status === status && t.id !== dragTask.id);
      const overIdx = colItems.findIndex(t => t.id === dragOverTaskId);
      const newOrder = [...colItems];
      newOrder.splice(overIdx, 0, dragTask);
      const orderedIds = newOrder.map(t => t.id);
      // Optimistic update
      setTasks(prev => {
        const others = prev.filter(t => t.status !== status);
        const updated = newOrder.map((t, i) => ({ ...t, sort_order: i }));
        return [...others, ...updated];
      });
      try {
        await apiFetch('/api/tasks/reorder', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ orderedIds }),
        });
      } catch {
        await load();
      }
    }

    setDragOver(null);
    setDragOverTaskId(null);
    setDragTask(null);
  };

  const quickCreateSubmit = async (status: string) => {
    if (!quickCreateTitle.trim()) { setQuickCreateStatus(null); setQuickCreateTitle(''); return; }
    try {
      await apiFetch('/api/tasks', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title: quickCreateTitle.trim(), status }),
      });
      toast('Задача создана');
      await load();
    } catch { toast('Ошибка создания', 'error'); }
    setQuickCreateStatus(null);
    setQuickCreateTitle('');
  };

  if (!loaded) return <TasksSkeleton />;

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-700">
        <div className="flex items-center gap-3">
          <h2 className="text-lg font-semibold text-white">Задачи</h2>
          <span className="text-xs text-slate-500 bg-slate-800 px-2 py-0.5 rounded-full">{totalActive} активных</span>
          {totalOverdue > 0 && (
            <span className="text-xs text-red-400 bg-red-500/10 px-2 py-0.5 rounded-full flex items-center gap-1">
              <AlertTriangle size={10} /> {totalOverdue} просрочено
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Поиск..."
              className="w-48 bg-slate-800 border border-slate-600 rounded-xl pl-8 pr-8 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/50 transition-all"
            />
            {search && (
              <button onClick={() => setSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white">
                <X size={12} />
              </button>
            )}
          </div>
          <button
            onClick={() => setShowArchive(!showArchive)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-sm transition-colors ${
              showArchive ? 'bg-green-600/20 text-green-300 border border-green-500/30' : 'bg-slate-800 text-slate-400 hover:text-white border border-transparent'
            }`}
          >
            <Archive size={16} />
            Архив
            {totalDone > 0 && <span className="text-[10px] bg-slate-700 px-1.5 py-0.5 rounded-full">{totalDone}</span>}
          </button>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`p-2 rounded-xl transition-colors ${showFilters ? 'bg-indigo-600 text-white' : 'bg-slate-800 text-slate-400 hover:text-white'}`}
          >
            <Filter size={18} />
          </button>
          <button
            onClick={exportTasksCSV}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-sm transition-colors bg-slate-800 text-slate-400 hover:text-white border border-transparent"
            title="Экспорт CSV"
          >
            <Download size={16} />
          </button>
          <button
            onClick={() => setShowAdd(!showAdd)}
            className="flex items-center gap-1.5 px-3.5 py-2 bg-indigo-600 hover:bg-indigo-500 text-white text-sm rounded-xl transition-colors font-medium"
          >
            <Plus size={18} /> Добавить
          </button>
        </div>
      </div>

      {/* Filters bar */}
      {showFilters && (
        <div className="flex items-center gap-2 px-4 py-2 border-b border-slate-700 bg-slate-800/30 animate-slide-down">
          {[
            { id: 'all' as FilterMode, label: 'Все' },
            { id: 'overdue' as FilterMode, label: 'Просрочено' },
            { id: 'today' as FilterMode, label: 'Сегодня' },
            { id: 'week' as FilterMode, label: 'Неделя' },
            { id: 'no-deadline' as FilterMode, label: 'Без срока' },
          ].map((f) => (
            <button
              key={f.id}
              onClick={() => setFilterMode(f.id)}
              className={`text-xs px-2.5 py-1 rounded-lg transition-colors ${filterMode === f.id ? 'bg-indigo-600/30 text-indigo-300 border border-indigo-500/30' : 'text-slate-400 hover:text-white bg-slate-800 border border-transparent'}`}
            >
              {f.label}
            </button>
          ))}
          <div className="w-px h-4 bg-slate-700 mx-1" />
          {[
            { id: 'all' as PriorityFilter, label: 'Все', color: '' },
            { id: '3' as PriorityFilter, label: '↑ Высокий', color: 'text-red-400' },
            { id: '2' as PriorityFilter, label: '→ Средний', color: 'text-amber-400' },
            { id: '1' as PriorityFilter, label: '↓ Низкий', color: 'text-green-400' },
          ].map((f) => (
            <button
              key={f.id}
              onClick={() => setPriorityFilter(f.id)}
              className={`text-xs px-2.5 py-1 rounded-lg transition-colors ${priorityFilter === f.id ? 'bg-indigo-600/30 text-indigo-300 border border-indigo-500/30' : `${f.color || 'text-slate-400'} hover:text-white bg-slate-800 border border-transparent`}`}
            >
              {f.label}
            </button>
          ))}
        </div>
      )}

      {loadError && (
        <div className="mx-4 mt-3 p-2 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-xs">{loadError}</div>
      )}

      {/* Add form */}
      {showAdd && (
        <div className="px-4 py-3 border-b border-slate-700 bg-slate-800/30 space-y-3 animate-slide-down">
          <input
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            placeholder="Название задачи"
            className="w-full bg-slate-800 border border-slate-600 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/50"
            autoFocus
            onKeyDown={(e) => e.key === 'Enter' && createTask()}
          />
          <textarea
            value={newDescription}
            onChange={(e) => setNewDescription(e.target.value)}
            placeholder="Описание (необязательно)"
            rows={2}
            className="w-full bg-slate-800 border border-slate-600 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/50 resize-none"
          />
          <div className="flex gap-3 items-center flex-wrap">
            <select
              value={newStatus}
              onChange={(e) => setNewStatus(e.target.value)}
              className="bg-slate-800 border border-slate-600 rounded-xl px-3 py-1.5 text-sm focus:outline-none"
            >
              {KANBAN_STATUSES.map((s) => (
                <option key={s} value={s}>{STATUS_CONFIG[s].label}</option>
              ))}
            </select>
            <select
              value={newPriority}
              onChange={(e) => setNewPriority(Number(e.target.value))}
              className="bg-slate-800 border border-slate-600 rounded-xl px-3 py-1.5 text-sm focus:outline-none"
            >
              {PRIORITY_CONFIG.map((p) => (
                <option key={p.value} value={p.value}>{p.label}</option>
              ))}
            </select>
            <div className="flex items-center gap-2">
              <Calendar size={14} className="text-slate-500" />
              <input
                type="date"
                value={newDeadline}
                onChange={(e) => setNewDeadline(e.target.value)}
                className="bg-slate-800 border border-slate-600 rounded-xl px-2 py-1.5 text-sm focus:outline-none"
              />
            </div>
            <button onClick={createTask} disabled={creating} className="px-4 py-1.5 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 text-white text-sm rounded-xl transition-colors ml-auto">
              {creating ? <Loader2 size={14} className="animate-spin" /> : 'Создать'}
            </button>
          </div>
        </div>
      )}

      {/* Kanban columns (3: Idea, Planned, In Progress) */}
      <div className={`${showArchive ? 'flex-none' : 'flex-1'} overflow-x-auto`} style={showArchive ? { height: '60%' } : undefined}>
        <div className="flex h-full min-w-[600px]">
          {tasksByStatus.map(({ status, config, items }) => {
            const Icon = config.icon;
            const isDragTarget = dragOver === status;
            return (
              <div
                key={status}
                className={`flex-1 flex flex-col border-r border-slate-800 last:border-r-0 transition-colors ${isDragTarget ? 'bg-indigo-500/5' : ''}`}
                onDragOver={(e) => handleDragOver(e, status)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, status)}
              >
                {/* Column header */}
                <div className={`px-3 py-2.5 border-b border-slate-700 flex items-center gap-2 ${config.headerBg}`}>
                  <Icon size={16} className={`${config.color} ${status === 'in_progress' ? 'animate-spin' : ''}`} />
                  <span className={`text-sm font-medium ${config.color}`}>{config.label}</span>
                  <span className="text-xs text-slate-500 ml-auto bg-slate-800/50 px-1.5 py-0.5 rounded-full">{items.length}</span>
                  <button
                    onClick={() => { setQuickCreateStatus(status); setQuickCreateTitle(''); }}
                    className="text-slate-600 hover:text-slate-300 transition-colors ml-1"
                    title="Быстрое создание (Enter)"
                  >
                    <Plus size={14} />
                  </button>
                </div>

                {isDragTarget && (
                  <div className="mx-2 mt-2 p-2 border-2 border-dashed border-indigo-500/30 rounded-xl text-center text-xs text-indigo-400">
                    Отпустите здесь
                  </div>
                )}

                {/* Tasks */}
                <div className="flex-1 overflow-y-auto p-2 space-y-2">
                  {items.map((task) => {
                    const prioConfig = PRIORITY_CONFIG[task.priority] || PRIORITY_CONFIG[0];
                    const PrioIcon = prioConfig.icon;
                    const overdue = isOverdue(task.deadline);
                    const linkedNote = task.note_id ? notes.find(n => n.id === task.note_id) : null;
                    return (
                      <div
                        key={task.id}
                        draggable
                        onDragStart={() => handleDragStart(task)}
                        onDragEnd={() => { setDragTask(null); setDragOver(null); setDragOverTaskId(null); }}
                        onDragOver={(e) => { handleDragOver(e, status); handleDragOverTask(e, task.id); }}
                        onClick={() => setModalTask(task)}
                        className={`group cursor-pointer rounded-xl overflow-hidden border transition-all ${
                          overdue
                            ? 'bg-red-500/5 border-red-500/20 hover:border-red-500/40'
                            : 'bg-slate-800/60 border-slate-700/50 hover:border-slate-500/60 hover:bg-slate-800/80'
                        } ${dragTask?.id === task.id ? 'opacity-40' : ''} ${lastMovedId === task.id ? 'animate-fade-in' : ''}`}
                      >
                        <div className="flex">
                          {/* Priority left bar */}
                          <div className={`w-0.5 shrink-0 ${
                            task.priority === 3 ? 'bg-red-500' :
                            task.priority === 2 ? 'bg-amber-400' :
                            task.priority === 1 ? 'bg-green-500' : 'bg-slate-700'
                          }`} />
                          <div className="flex-1 p-3 min-w-0">
                            {/* Title row */}
                            <div className="flex items-start justify-between gap-1.5 mb-1">
                              <div className="flex items-start gap-1.5 flex-1 min-w-0">
                                {PrioIcon && (
                                  <span className={`mt-0.5 shrink-0 ${prioConfig.color}`}>
                                    <PrioIcon size={11} />
                                  </span>
                                )}
                                <p className="text-[13px] font-medium text-white leading-snug line-clamp-2">{task.title}</p>
                              </div>
                              <button
                                onClick={(e) => { e.stopPropagation(); updateStatus(task, 'done'); }}
                                className="text-slate-600 hover:text-green-400 opacity-0 group-hover:opacity-100 transition-all shrink-0 mt-0.5"
                                title="Завершить"
                              >
                                <CheckCircle2 size={14} />
                              </button>
                            </div>

                            {/* Description */}
                            {task.description && (
                              <p className="text-[11px] text-slate-500 leading-relaxed line-clamp-2 mb-2.5 pl-4">{task.description}</p>
                            )}

                            {/* Subtask progress */}
                            {(() => {
                              const subs = tasks.filter(t => t.parent_id === task.id);
                              if (subs.length === 0) return null;
                              const done = subs.filter(s => s.status === 'done').length;
                              return (
                                <div className="flex items-center gap-1.5 mb-2 pl-4">
                                  <div className="flex-1 h-1 bg-slate-700 rounded-full overflow-hidden">
                                    <div className="h-full bg-green-500 rounded-full transition-all" style={{ width: `${(done / subs.length) * 100}%` }} />
                                  </div>
                                  <span className="text-[10px] text-slate-500">{done}/{subs.length}</span>
                                </div>
                              );
                            })()}

                            {/* Footer badges */}
                            <div className="flex items-center gap-1.5 flex-wrap">
                              {editingDeadlineId === task.id ? (
                                <input
                                  type="date"
                                  defaultValue={task.deadline || ''}
                                  className="text-[10px] px-1.5 py-0.5 rounded-md bg-slate-800 border border-indigo-500/50 text-slate-300 focus:outline-none"
                                  autoFocus
                                  onClick={(e) => e.stopPropagation()}
                                  onBlur={(e) => {
                                    const val = e.target.value;
                                    updateTask(task.id, { deadline: val || null });
                                    setEditingDeadlineId(null);
                                  }}
                                  onKeyDown={(e) => {
                                    e.stopPropagation();
                                    if (e.key === 'Enter') {
                                      updateTask(task.id, { deadline: (e.target as HTMLInputElement).value || null });
                                      setEditingDeadlineId(null);
                                    } else if (e.key === 'Escape') {
                                      setEditingDeadlineId(null);
                                    }
                                  }}
                                />
                              ) : task.deadline ? (
                                <span
                                  onClick={(e) => { e.stopPropagation(); setEditingDeadlineId(task.id); }}
                                  className={`text-[10px] flex items-center gap-1 px-1.5 py-0.5 rounded-md border cursor-pointer hover:border-indigo-500/50 ${
                                  overdue
                                    ? 'text-red-400 bg-red-500/10 border-red-500/20'
                                    : isToday(task.deadline) ? 'text-amber-400 bg-amber-500/10 border-amber-500/20'
                                    : 'text-slate-500 bg-slate-800 border-slate-700'
                                }`}>
                                  <Calendar size={9} />
                                  {new Date(task.deadline).toLocaleDateString('ru-RU')}
                                </span>
                              ) : (
                                <span
                                  onClick={(e) => { e.stopPropagation(); setEditingDeadlineId(task.id); }}
                                  className="text-[10px] flex items-center gap-1 px-1.5 py-0.5 rounded-md border border-dashed border-slate-700 text-slate-600 cursor-pointer hover:border-slate-500 hover:text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity"
                                >
                                  <Calendar size={9} /> Срок
                                </span>
                              )}
                              {linkedNote && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); if (onNavigateToNote) onNavigateToNote(linkedNote.id); }}
                                  className="text-[10px] flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-indigo-500/10 border border-indigo-500/20 text-indigo-400 hover:bg-indigo-500/20 transition-colors max-w-[110px]"
                                >
                                  <FileText size={9} />
                                  <span className="truncate">{linkedNote.title}</span>
                                </button>
                              )}

                              {/* Status arrows */}
                              <div className="flex gap-0.5 ml-auto opacity-0 group-hover:opacity-100 transition-opacity">
                                {status !== 'idea' && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      const idx = KANBAN_STATUSES.indexOf(status);
                                      if (idx > 0) updateStatus(task, KANBAN_STATUSES[idx - 1]);
                                    }}
                                    className="text-slate-600 hover:text-indigo-400 p-0.5 rounded"
                                    title={`← ${STATUS_CONFIG[KANBAN_STATUSES[KANBAN_STATUSES.indexOf(status) - 1]]?.label}`}
                                  >
                                    <ChevronLeft size={12} />
                                  </button>
                                )}
                                {status !== 'in_progress' && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      const idx = KANBAN_STATUSES.indexOf(status);
                                      if (idx < KANBAN_STATUSES.length - 1) updateStatus(task, KANBAN_STATUSES[idx + 1]);
                                    }}
                                    className="text-slate-600 hover:text-indigo-400 p-0.5 rounded"
                                    title={`→ ${STATUS_CONFIG[KANBAN_STATUSES[KANBAN_STATUSES.indexOf(status) + 1]]?.label}`}
                                  >
                                    <ChevronRight size={12} />
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  {/* Quick-create input */}
                  {quickCreateStatus === status ? (
                    <div className="p-2">
                      <input
                        autoFocus
                        value={quickCreateTitle}
                        onChange={e => setQuickCreateTitle(e.target.value)}
                        onKeyDown={e => {
                          if (e.key === 'Enter') { e.preventDefault(); quickCreateSubmit(status); }
                          if (e.key === 'Escape') { setQuickCreateStatus(null); setQuickCreateTitle(''); }
                        }}
                        onBlur={() => quickCreateSubmit(status)}
                        placeholder="Название задачи..."
                        className="w-full bg-slate-800 border border-slate-600 focus:border-indigo-500 rounded-xl px-3 py-2 text-sm text-white placeholder-slate-500 outline-none"
                      />
                      <p className="text-[10px] text-slate-600 mt-1 px-1">Enter — создать · Esc — отмена</p>
                    </div>
                  ) : (
                    <button
                      onClick={() => { setQuickCreateStatus(status); setQuickCreateTitle(''); }}
                      className="w-full py-2 text-xs text-slate-600 hover:text-slate-400 flex items-center justify-center gap-1 rounded-xl hover:bg-slate-800/50 transition-all"
                    >
                      <Plus size={12} /> Добавить задачу
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Archive section */}
      {showArchive && (
        <div className="flex-1 border-t border-slate-700 flex flex-col overflow-hidden animate-slide-up">
          <div className="px-4 py-2 bg-green-600/5 border-b border-slate-700 flex items-center gap-2">
            <CheckCircle2 size={14} className="text-green-400" />
            <span className="text-sm font-medium text-green-400">Архив выполненных</span>
            <span className="text-xs text-slate-500 bg-slate-800 px-1.5 py-0.5 rounded-full">{archiveTasks.length}</span>
          </div>
          <div className="flex-1 overflow-y-auto p-3 space-y-1.5">
            {archiveTasks.map((task) => {
              const prioConfig = PRIORITY_CONFIG[task.priority] || PRIORITY_CONFIG[0];
              const PrioIcon = prioConfig.icon;
              return (
                <div
                  key={task.id}
                  onClick={() => setModalTask(task)}
                  className="flex items-center gap-3 bg-slate-800/30 rounded-xl px-3 py-2.5 group hover:bg-slate-800/50 transition-colors cursor-pointer"
                >
                  <CheckCircle2 size={16} className="text-green-500 shrink-0" />
                  <span className={`shrink-0 ${prioConfig.color}`}>
                    {PrioIcon ? <PrioIcon size={12} /> : null}
                  </span>
                  <span className="text-sm text-slate-400 line-through flex-1 truncate">{task.title}</span>
                  {task.deadline && (
                    <span className="text-[11px] text-slate-600">
                      {new Date(task.deadline).toLocaleDateString('ru-RU')}
                    </span>
                  )}
                  <button
                    onClick={(e) => { e.stopPropagation(); updateStatus(task, 'in_progress'); }}
                    className="text-slate-600 hover:text-amber-400 opacity-0 group-hover:opacity-100 transition-all text-xs"
                    title="Вернуть в работу"
                  >
                    ↩
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); deleteTask(task.id); }}
                    className="text-slate-600 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-all"
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              );
            })}
            {archiveTasks.length === 0 && (
              <p className="text-center text-slate-600 text-sm py-6">Нет завершённых задач</p>
            )}
          </div>
        </div>
      )}

      {/* Task modal */}
      {modalTask && (
        <TaskModal
          task={modalTask}
          notes={notes}
          allTasks={tasks}
          onClose={() => setModalTask(null)}
          onSave={updateTask}
          onDelete={deleteTask}
          onNavigateToNote={onNavigateToNote}
          onCreateSubtask={async (title, parentId) => {
            await apiFetch('/api/tasks', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ title, parent_id: parentId, status: 'idea' }),
            });
          }}
          onReload={load}
        />
      )}
    </div>
  );
}
